import { useState, useEffect, SyntheticEvent } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Button, Divider, FormControl,
  TextField, InputLabel, MenuItem, Select, useMediaQuery,
  Checkbox, FormGroup, FormControlLabel, SelectChangeEvent, Paper, CircularProgress
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import axios from "axios";
import Title from "../../components/Title";
import SearchIcon from '@mui/icons-material/Search';
import { useDrawer } from "../../components/context/DrawerContext";
import { stickyListRegionWidth } from "../../components/layouts/StickyListToolbar";
import SimpleCustomTable from "../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow, SimpleColumnGroup, RowId } from "../../components/table/interface";
import HeaderResponsive from "../../components/layouts/HeaderResponsive";
import EmptyStateMessage from "../../components/layouts/EmptyStateMessage";
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';

// Interfaces mínimas para filtros
interface Office { id: number; name: string; }
interface Collect { id: number; name: string; }

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;          // string para que no se limpie el Select
  selectedCollects: number[];
}

interface ReporteDescuadresProps {
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Utils
const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(value);

const formatDate = (dateString?: string): string => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString; // por si ya viene YYYY-MM-DD, lo dejamos
  return d.toLocaleDateString("es-CO");
};

const SummaryBox = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[0],
  border: `1px solid ${theme.palette.divider}`,
}));

// Componente
const ReporteDescuadres: React.FC<ReporteDescuadresProps> = ({
  showAlert, showLoading, hideLoading,
}) => {
  document.title = "Reporte Descuadre";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentDate = nowOperationalDateString();

  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const { drawerOpen } = useDrawer();

  // filtro
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedCollects: [],
  });

  // datos UI
  const [rows, setRows] = useState<SimpleRow[]>([]);
  const [totalShortOver, setTotalShortOver] = useState<number>(0);

  // Oficinas
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/offices");
        if (data?.data) setOffices(data.data);
      } catch (e) {
        console.error(e);
        showAlert("Error al cargar las oficinas.", "error");
      }
    })();
  }, [showAlert]);

  // Cobros por oficina
  useEffect(() => {
    (async () => {
      if (!filter.officeId) return;
      setLoading(true);
      try {
        const { data } = await axios.get(`/collectsoffice/${filter.officeId}`);
        setCollectsOffice(data || []);
        setFilter((prev) => ({ ...prev, selectedCollects: [] }));
        setSelectAll(false);
      } catch (e) {
        console.error(e);
        showAlert("Error al cargar los cobros para la oficina.", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [filter.officeId, showAlert]);

  // handlers
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };
  const handleOfficeChange = (e: SelectChangeEvent<string>) => {
    setFilter((prev) => ({ ...prev, officeId: e.target.value }));
  };
  const handleSelectAllCollects = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSelectAll(checked);
    setFilter((prev) => ({
      ...prev,
      selectedCollects: checked ? collectsOffice.map((c) => c.id) : [],
    }));
  };
  const handleCollectChange = (collectId: number, checked: boolean) => {
    setFilter((prev) => {
      const set = new Set(prev.selectedCollects);
      if (checked) set.add(collectId); else set.delete(collectId);
      const next = Array.from(set);
      setSelectAll(next.length === collectsOffice.length && collectsOffice.length > 0);
      return { ...prev, selectedCollects: next };
    });
  };

  const validateForm = (): boolean => {
    if (!filter.officeId) {
      showAlert("Seleccione una oficina.", "warning");
      return false;
    }
    if (filter.selectedCollects.length === 0) {
      showAlert("Debe seleccionar al menos un cobro.", "warning");
      return false;
    }
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert("La fecha de inicio no puede ser mayor a la fecha final.", "warning");
      return false;
    }
    return true;
  };

  const baseColumns: SimpleColumn[] = [
    {
      field: 'date',
      headerName: 'Fecha',
      flex: 1,
      minWidth: 140,
      renderCell: ({ row }: { row: SimpleRow }) => formatDate(row.date as string),
    },
    { field: 'idnumber', headerName: 'Cédula', flex: 1, minWidth: 140 },
    { field: 'name', headerName: 'Nombre', flex: 1.6, minWidth: 240, wrapText: true },
    { field: 'collect', headerName: 'Cobro', flex: 1.2, minWidth: 180 },
    {
      field: 'shortOver',
      headerName: 'Descuadre',
      flex: 1,
      minWidth: 150,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) => formatCurrency(Number(row.shortOver ?? 0)),
    },
  ];

  const simpleColumns: SimpleColumn[] = baseColumns;
  const mobileColumns: SimpleColumn[] = [
    baseColumns.find(col => col.field === 'date')!,
    baseColumns.find(col => col.field === 'name')!,
    baseColumns.find(col => col.field === 'shortOver')!,
  ];

  const displayColumns = isMobile ? mobileColumns : simpleColumns;
  const columnGroups: SimpleColumnGroup[] = [];
  const displayColumnGroups: SimpleColumnGroup[] = [];

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    try {
      const payload = {
        start_date: filter.startDate,      // "YYYY-MM-DD"
        end_date: filter.endDate,          // "YYYY-MM-DD"
        collect_Id: filter.selectedCollects,
      };

      const { data } = await axios.post("/inform/cashshortover", payload);

      const items: SimpleRow[] = (data?.data || [])
        .map((item: any, index: number) => ({
          id: (item.id ?? index) as RowId,
          date: item.date,
          idnumber: item.idnumber,
          name: item.name,
          collect: item.collect,
          collect_id: item.collect_id,
          shortOver: item.shortOver,
        }))
        .sort((a: SimpleRow, b: SimpleRow) => {
          const aDate = moment(a.date as string);
          const bDate = moment(b.date as string);

          const byDateDesc = bDate.valueOf() - aDate.valueOf();
          if (byDateDesc !== 0) return byDateDesc;

          return String(a.collect ?? "").localeCompare(String(b.collect ?? ""), "es", {
            sensitivity: "base",
          });
        });

      if (!items.length) {
        setRows([]);
        setTotalShortOver(0);
        showAlert("No se encontraron registros en el período.", "info");
        return;
      }

      setRows(items);

      const total = items.reduce((acc, r) => acc + (r.shortOver || 0), 0);
      setTotalShortOver(total);

      showAlert(`Se encontraron ${items.length} registros.`, "success");
    } catch (error) {
      console.error("Error obteniendo datos:", error);
      showAlert("Error al obtener los datos.", "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Title>Resumen Descuadre</Title>
        <Box>
          <Button type="submit" variant="contained" startIcon={!isMobile && <SearchIcon />} form="form-descuadre">
            {isMobile ? <SearchIcon /> : 'Buscar'}
          </Button>
        </Box>
      </Box>

      <Card sx={{ width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <form id="form-descuadre" onSubmit={handleSubmit}>
          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen),
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
              flexShrink: 0,
              scrollbarColor: '#0768c9 #d9e1ea',
              '&::-webkit-scrollbar': {
                width: 12,
                height: 12,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#d9e1ea',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#0768c9',
                borderRadius: 999,
                border: '2px solid #d9e1ea',
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: '#d9e1ea',
              },
            }}
          >
            <Grid container spacing={2}>
              {/* Fechas */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth name="startDate" size="small" type="date" label="Fecha Inicio"
                  value={filter.startDate} onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }} inputProps={{ max: minYmd(filter.endDate, nowOperationalDateString()) }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth name="endDate" size="small" type="date" label="Fecha Fin"
                  value={filter.endDate} onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }} inputProps={{ min: filter.startDate, max: nowOperationalDateString() }}
                />
              </Grid>

              {/* Oficina (value como string para no perder selección) */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Oficina</InputLabel>
                  <Select value={filter.officeId} size="small" label="Oficina" onChange={handleOfficeChange}>
                    {offices.map((office) => (
                      <MenuItem key={office.id} value={String(office.id)}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Cobros */}
              {collectsOffice.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                      Seleccionar Cobros:
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={filter.selectedCollects.length === collectsOffice.length && collectsOffice.length > 0}
                          onChange={handleSelectAllCollects}
                          indeterminate={
                            filter.selectedCollects.length > 0 &&
                            filter.selectedCollects.length < collectsOffice.length
                          }
                        />
                      }
                      label="Marcar/Desmarcar todos"
                    />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <FormGroup row>
                    {collectsOffice.map((collect) => (
                      <FormControlLabel
                        key={collect.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={filter.selectedCollects.includes(collect.id)}
                            onChange={(e) => handleCollectChange(collect.id, e.target.checked)}
                            value={collect.id.toString()}
                          />
                        }
                        label={collect.name}
                        sx={{ minWidth: "200px", width: isMobile ? "100%" : "auto", m: 0.5 }}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              )}
            </Grid>
            <Divider sx={{ my: 1 }} />
          </CardContent>
        </form>

        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
            pt: 0,
            pb: 4,
          }}
        >
          {rows.length > 0 ? (
            <>
              <SimpleCustomTable
                rows={rows}
                columns={displayColumns}
                disableInternalScroll
                showBorders
                showCount
                exportCsv
                exportFileName="resumen-descuadre.csv"
                wrapCellContent
                columnGroups={displayColumnGroups}
              />
            </>
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />

      {/* Loader overlay opcional (no desmonta) */}
      {loading && (
        <Box sx={{
          position: 'fixed', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.12)', zIndex: 2000
        }}>
          <CircularProgress />
        </Box>
      )}
    </HeaderResponsive>
  );
};

export default ReporteDescuadres;
